import { useRef } from "react";
import { toast } from "sonner";
import { ImagePlus, Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n";

export const PRINTS_MAX = 8;
const OK_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_SIDE = 1800;

const compress = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    resolve(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
  img.src = url;
});

const btnCls = "flex-1 flex items-center justify-center gap-2 bg-white border-[3px] border-black rounded-xl py-3 font-bold uppercase text-xs sm:text-sm tracking-wide shadow-[4px_4px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]";

export const PrintsThumbs = ({ prints, onMove, onRemove }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="prints-list">
    {prints.map((p, i) => (
      <div key={p.id} className="relative border-[3px] border-black rounded-xl overflow-hidden bg-white" data-testid={`print-item-${i}`}>
        <img src={p.src} alt={`print ${i + 1}`} className="w-full h-28 object-cover object-top" />
        <span className="absolute top-1 left-1 bg-[#FFD500] border-[2px] border-black rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">{i + 1}</span>
        {onRemove && (
          <button data-testid={`print-remove-${i}`} onClick={() => onRemove(i)} aria-label="remover"
            className="absolute top-1 right-1 bg-white border-[2px] border-black rounded-full p-0.5 hover:bg-[#FF3366] hover:text-white transition-colors">
            <X size={14} strokeWidth={3} />
          </button>
        )}
        {onMove && (
          <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/70">
            <button data-testid={`print-left-${i}`} onClick={() => onMove(i, -1)} disabled={i === 0} className="text-white p-1 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} /></button>
            <button data-testid={`print-right-${i}`} onClick={() => onMove(i, 1)} disabled={i === prints.length - 1} className="text-white p-1 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} /></button>
          </div>
        )}
      </div>
    ))}
  </div>
);

export const PrintsPicker = ({ prints, setPrints }) => {
  const { t } = useLang();
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const bad = files.filter((f) => !OK_TYPES.includes(f.type));
    if (bad.length) toast.error(t.pr_bad_format);
    const room = PRINTS_MAX - prints.length;
    const good = files.filter((f) => OK_TYPES.includes(f.type));
    if (good.length > room) toast.error(t.pr_too_many);
    const added = [];
    for (const f of good.slice(0, Math.max(0, room))) {
      try { added.push({ id: `${Date.now()}-${Math.random()}`, src: await compress(f) }); }
      catch (e) { console.error("image decode failed:", e); toast.error(t.pr_bad_format); }
    }
    if (added.length) setPrints((p) => [...p, ...added]);
  };

  const onPick = (e) => { addFiles(e.target.files); e.target.value = ""; };
  const move = (i, d) => setPrints((p) => { const n = [...p]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const remove = (i) => setPrints((p) => p.filter((_, k) => k !== i));

  return (
    <div className="space-y-3" data-testid="prints-picker">
      <p className="text-sm font-semibold text-[#222] bg-[#FFD500] border-[2px] border-black rounded-lg px-3 py-2">{t.pr_order_hint}</p>
      <p className="text-xs font-semibold text-[#666]">{t.pr_privacy_hint}</p>
      <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={onPick} data-testid="prints-gallery-input" />
      <input ref={cameraRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={onPick} data-testid="prints-camera-input" />
      <div className="flex gap-3">
        <button data-testid="prints-gallery-btn" onClick={() => galleryRef.current?.click()} disabled={prints.length >= PRINTS_MAX} className={`${btnCls} disabled:opacity-50`}>
          <ImagePlus size={18} strokeWidth={2.5} /> {prints.length ? t.pr_add_more : t.pr_gallery}
        </button>
        <button data-testid="prints-camera-btn" onClick={() => cameraRef.current?.click()} disabled={prints.length >= PRINTS_MAX} className={`${btnCls} disabled:opacity-50`}>
          <Camera size={18} strokeWidth={2.5} /> {t.pr_camera}
        </button>
      </div>
      {prints.length > 0 && <PrintsThumbs prints={prints} onMove={move} onRemove={remove} />}
      <p data-testid="prints-count" className="text-xs font-bold text-right text-[#888]">{prints.length} / {PRINTS_MAX}</p>
    </div>
  );
};
